import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

export const WORK_STAGES = [
  { key: "SURVEY", label: "Site Survey", prerequisite: null },
  { key: "SOIL_TEST", label: "Soil Test", prerequisite: "SURVEY" },
  { key: "DESIGN", label: "Drawing & Design", prerequisite: "SOIL_TEST" },
  { key: "ESTIMATE", label: "Estimate / BOQ", prerequisite: "DESIGN" },
  { key: "DPR_CA", label: "Final DPR", prerequisite: "ESTIMATE" },
];

export const clean = (v = "") => String(v || "").trim();
export const upper = (v = "") => clean(v).toUpperCase();

export function isDeleted(row = {}) {
  return (
    row.isDeleted === true ||
    row.deleted === true ||
    !!row.deletedAt ||
    ["DELETED", "REMOVED", "TRASHED", "ARCHIVED"].includes(
      upper(row.status || row.applicationStatus),
    )
  );
}

export function isNafedProject(row = {}) {
  return (
    !isDeleted(row) &&
    (row.nafedProject === true ||
      upper(row.projectSource) === "NAFED" ||
      upper(row.applicationPortal) === "NAFED_PACS_APPLY")
  );
}

export function isMouExecuted(row = {}) {
  const values = [
    row.mouStatus,
    row.mouExecutionStatus,
    row.workflowStage,
    row.currentStage,
    row.projectStage,
    row.status,
  ].map(upper);
  return (
    row.mouFullyExecuted === true ||
    row.threePartyMouExecuted === true ||
    !!row.finalExecutedMouUrl ||
    values.some((v) =>
      [
        "MOU_FULLY_EXECUTED",
        "FULLY_EXECUTED",
        "THREE_PARTY_MOU_FULLY_EXECUTED",
        "MOU_COMPLETED",
      ].includes(v),
    )
  );
}

export function getPmcUid(row = {}) {
  return clean(
    row.allocatedPmcUid ||
      row.assignedPmcUid ||
      row.pmcUid ||
      row.allocatedPmcId ||
      row.assignedPmcId ||
      row.pmcId,
  );
}

export function stageState(tasks = [], stageKey) {
  const task = tasks.find((item) => upper(item.type) === stageKey);
  return task
    ? upper(task.adminStatus || task.pmcStatus || task.status)
    : "NOT_ALLOCATED";
}

export function canAllocateStage(tasks = [], stageKey) {
  const stage = WORK_STAGES.find((item) => item.key === stageKey);
  if (!stage) return false;
  if (tasks.some((item) => upper(item.type) === stageKey)) return false;
  if (!stage.prerequisite) return true;
  return stageState(tasks, stage.prerequisite) === "APPROVED";
}

export function nextAvailableStage(tasks = []) {
  return (
    WORK_STAGES.find((stage) => canAllocateStage(tasks, stage.key)) || null
  );
}

export function mapsUrl(row = {}) {
  if (row.googleMapsUrl) return row.googleMapsUrl;
  if (row.latitude && row.longitude) {
    return `https://www.google.com/maps?q=${row.latitude},${row.longitude}`;
  }
  const address = [
    row.village || row.locationVillage,
    row.taluka,
    row.district,
    row.state,
  ]
    .filter(Boolean)
    .join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

export async function syncEligibleNafedProjects(db) {
  const sourceSnap = await getDocs(collection(db, "godown_projects"));
  const synced = [];
  for (const sourceDoc of sourceSnap.docs) {
    const source = { id: sourceDoc.id, ...sourceDoc.data() };
    if (!isNafedProject(source) || !isMouExecuted(source) || !getPmcUid(source))
      continue;
    const payload = {
      sourceProjectId: source.id,
      nafedProject: true,
      projectSource: "NAFED",
      applicationPortal: "NAFED_PACS_APPLY",
      title:
        source.projectName ||
        source.title ||
        source.societyName ||
        source.pacsName ||
        "NAFED PACS Project",
      pacsUid: source.pacsUid || source.pacsId || source.applicantUid || "",
      pacsName:
        source.pacsName || source.societyName || source.organizationName || "",
      applicationId: source.applicationId || source.projectId || source.id,
      capacityMt: Number(
        source.capacityMt ||
          source.capacityMT ||
          source.capacity ||
          source.godownCapacity ||
          0,
      ),
      state: source.state || source.stateName || "",
      district: source.district || source.districtName || "",
      taluka: source.taluka || source.block || "",
      locationVillage: source.village || source.locationVillage || "",
      address: source.address || source.fullAddress || "",
      googleMapsUrl: mapsUrl(source),
      allocatedPmcUid: getPmcUid(source),
      allocatedPmcName:
        source.allocatedPmcName ||
        source.assignedPmcName ||
        source.pmcName ||
        "",
      mouStatus: "FULLY_EXECUTED",
      finalExecutedMouUrl:
        source.finalExecutedMouUrl || source.executedMouUrl || "",
      status: "MOU_COMPLETED",
      syncStatus: "ACTIVE",
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, "wf_projects", source.id), payload, { merge: true });
    synced.push({ id: source.id, ...payload });
  }
  return synced;
}

export async function loadProjectTasks(db, projectId) {
  const snap = await getDocs(
    query(
      collection(db, "wf_project_tasks"),
      where("projectId", "==", projectId),
    ),
  );
  return snap.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function queueWorkflowMail(
  db,
  { to, subject, html, event, projectId },
) {
  if (!to) return;
  const ref = doc(collection(db, "mail"));
  await setDoc(ref, {
    to: [to],
    message: { subject, html },
    metadata: { event, projectId: projectId || "" },
    createdAt: serverTimestamp(),
  });
}

export async function getProfile(db, uid) {
  if (!uid) return null;
  const snap = await getDoc(doc(db, "nafed_users", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
